/**
 * countdown.event adapter — ClientAdapter for countdown.event tiles.
 *
 * countdown.event is a noFetch, clientDirect tile. Params ARE the storage:
 * { label: string, targetISO: string }. Data is computed from params + the
 * current time (no network, no upstream).
 *
 * GLANCE RULINGS (glance-grammar-spec §1.3 / D-015):
 *   primary : Time-remaining/elapsed, formatted to fit ≤8 chars:
 *             - daysLeft >= 1: `${daysLeft}d`         e.g. "7d", "365d"
 *             - daysLeft === 0 (within same 24h window, future): `${hoursLeft}h`
 *               e.g. "3h", "23h". This handles "today" gracefully.
 *             - daysLeft < 0 (past), whole display in "since" mode:
 *               Absolute value of daysLeft: `${Math.abs(daysLeft)}d ago`
 *               BUT "365d ago" = 9 chars → over limit. So we use:
 *               abs <= 9999: `${abs}d ago` if abs <= 4 (fits "4d ago" = 6 chars)
 *               Decision (FINAL): use `${abs}d ago` for past days 1..99 (max "99d ago"=7 chars)
 *               For 100+ days past: `${abs}d` (omit "ago") to stay ≤8 chars ("9999d" = 5 chars).
 *               For 0 days past (hoursLeft <= 0, within same 24h after target):
 *               `${Math.abs(hoursLeft)}h ago` if fits, else `${Math.abs(hoursLeft)}h`.
 *               Rule: abs(hoursLeft) 1..9 → `${abs}h ago` (6 chars), 10+ → `${abs}h`.
 *
 *   delta   : targetLabel (the user's event label), ≤20 chars via central clamp.
 *   state   : 'live' when <24h UNTIL the target (daysLeft === 0 AND diff > 0),
 *             'neutral' otherwise (including past events).
 *
 * PRIMARY FORMAT DECISION:
 *   The 8-char primary slot is very tight. The format table:
 *     daysLeft >= 1:         `${daysLeft}d`          [2–7 chars: "1d".."9999d"]
 *     daysLeft == 0, future: `${hoursLeft}h`         [2–3 chars: "0h".."23h"]
 *     daysLeft == 0, past:   same as below (hoursLeft <= 0)
 *     past, hours 0..9:      `${Math.abs(hoursLeft)}h ago` [6 chars: "0h ago".."9h ago"]
 *     past, hours 10..99:    `${Math.abs(hoursLeft)}h ago` [7 chars: "10h ago".."99h ago"]  ← 7 ≤ 8 ✓
 *     past, hours 100+:      `${Math.abs(hoursLeft)}h`    [avoid "100h ago" = 8 chars but risky]
 *     past, days 1..99:      `${Math.abs(daysLeft)}d ago` [6–7 chars: "1d ago".."99d ago"]
 *     past, days 100+:       `${Math.abs(daysLeft)}d`     [4–7 chars: "100d".."9999d"]
 *
 *   Summary: the "ago" suffix is included for < 100 days/hours past (all fit ≤7 chars);
 *   for 100+ it's dropped (the "d" / "h" makes it clear it's a count).
 *
 * Immutability: the exported adapter object is frozen; glanceView returns new.
 */
import { z } from 'zod';
import {
  countdownParamsSchema,
  deriveCacheKey,
  type CountdownParams,
} from 'briefpane/schema';
import type { GlanceMap } from 'briefpane/schema';
import type { ClientAdapter, GlanceViewModel } from 'briefpane/contracts';
import { computeCountdownData, type CountdownData } from './compute.js';

/** Zod shape for CountdownData — mirrors the fields CountdownData declares. */
export const countdownDataShape = z.object({
  targetLabel: z.string(),
  daysLeft: z.number().int(),
  hoursLeft: z.number().int(),
  sinceOrUntil: z.enum(['until', 'since']),
});

/**
 * Format the countdown primary slot.
 *
 * Kept pure and separate so it can be unit-tested directly.
 * All output ≤ 8 chars (guaranteed by the format rules in the docblock).
 */
export function formatCountdownPrimary(data: CountdownData): string {
  const { daysLeft, hoursLeft, sinceOrUntil } = data;

  if (sinceOrUntil === 'until') {
    // Future: days >= 1 → show days; day 0 (within 24h) → show hours.
    if (daysLeft >= 1) {
      return `${daysLeft}d`;
    }
    // daysLeft === 0: target is within the next 24h
    // hoursLeft is positive (could be 0 if < 1h, show 0h)
    return `${Math.max(0, hoursLeft)}h`;
  }

  // Past (sinceOrUntil === 'since'): both daysLeft and hoursLeft are <= 0.
  const absDays = Math.abs(daysLeft);
  const absHours = Math.abs(hoursLeft);

  if (absDays >= 1) {
    // Use day-based: "1d ago".."99d ago" (≤7 chars), "100d".."9999d" (≤7 chars)
    if (absDays < 100) {
      return `${absDays}d ago`;
    }
    return `${absDays}d`;
  }

  // Same day, already past: use hours. "0h ago".."99h ago" (≤7 chars), "100h".."999h"
  if (absHours < 100) {
    return `${absHours}h ago`;
  }
  return `${absHours}h`;
}

/**
 * Map CountdownData + GlanceMap → GlanceViewModel.
 *
 * Ruling: see file docblock. Central clamp guarantees ≤8 primary, ≤20 delta.
 */
export function countdownGlanceView(data: CountdownData, _glance: GlanceMap): GlanceViewModel {
  const primary = formatCountdownPrimary(data);
  const delta = data.targetLabel;

  // 'live' state: less than 24h until target (daysLeft === 0, sinceOrUntil = 'until')
  const isLive = data.sinceOrUntil === 'until' && data.daysLeft === 0;

  return {
    dataState: 'ok',
    glanceState: isLive ? 'live' : 'neutral',
    slots: { primary, delta },
    label: null,
    fetchedAt: Date.now(),
  };
}

export const countdownAdapter: ClientAdapter<CountdownParams, CountdownData> = Object.freeze({
  type: 'countdown.event' as const,
  provider: 'local' as const,
  tier: 'free' as const,
  ttlSec: 0,
  paramsSchema: countdownParamsSchema,
  dataShape: countdownDataShape,

  cacheKey: (params: CountdownParams): string =>
    deriveCacheKey({ type: 'countdown.event', provider: 'local', params }),

  glanceView: (data: CountdownData, glance: GlanceMap): GlanceViewModel =>
    countdownGlanceView(data, glance),
});

/** Compute CountdownData from params and current time (exported for use-tile-view-model). */
export { computeCountdownData };
